<template>
    <div class="w-full absolute top-0">
        <main class="container pt-34 mb-28 grid grid-cols-3 md:grid-cols-12">
            <h1 class="view-h1 col-span-full lg:col-start-2 mb-16 !col-start-1">
                {{ t('title') }}
            </h1>

                <div
                    v-if="featuredPosts.length"
                    class="col-span-full grid grid-cols-3 md:grid-cols-12 gap-0 gap-8 mb-0 md:mb-16"
                >
                    <BlogCard v-for="(post, index) in featuredPosts" :post="post" size="lg"/>

                </div>

                <div
                    v-if="highlightedPosts.length"
                    class="col-span-full grid grid-cols-3 md:grid-cols-12 gap-0 md:gap-8 mb-0 md:mb-16"
                >
                    <BlogCard v-for="(post, index) in highlightedPosts" :post="post" size="md"/>
                </div>

                <div
                    v-if="remainingPosts.length"
                    class="col-span-full grid grid-cols-3 md:grid-cols-12 gap-0 mt-0 md:mt-8"
                >
                    <BlogCard v-for="(post, index) in highlightedPosts" :post="post" size="sm"/>
                </div>

                <p
                    v-if="content.length === 0"
                    class="view-body col-span-full lg:col-start-2 lg:col-span-10"
                >
                    {{ t('empty') }}
                </p>

        </main>
        <Footer />
    </div>
</template>

<script lang="ts" setup>
import { Pages } from "#shared/types/Pages"
import Footer from "~/components/view/Footer.vue"
import type { ApiResponse } from "#layers/cms/shared/types/ApiResponse"
import type { BucketData } from "#layers/cms/shared/types/bucketData"
import BlogCard from "~/components/view/blog/BlogCard.vue"

const { t, locale } = useI18n({useScope: 'local'})
const { get_value: getLocalizedValue } = useLocalizedValue()

const mainStore = useMainStore()
const siteStore = useSiteStore()
const localePath = useLocalePath()
const config = useRuntimeConfig()

const siteUrl = String(config.public.publicHost || 'https://yipp.nl').replace(/\/$/, '')
const canonicalUrl = computed(() => `${siteUrl}${localePath('/insights', locale.value)}`)

useHead(() => ({
    title: t('seo.title')
}))
useSeoMeta({
    description: () => t('seo.description'),
    ogTitle: () => t('seo.title'),
    ogDescription: () => t('seo.description'),
    ogUrl: () => canonicalUrl.value
})

const { data: response } = await useAsyncData(
    'website-blog',
    () => $fetch<ApiResponse<BucketData[]>>(
        '/api/bucketContent/blog?live_only=true'
    )
)

if (!response.value) throw createError('Insights content could not be loaded')
if (!response.value.success) throw createError(response.value.message)

const content = response.value.data ?? []
const featuredPosts = computed(() => content.slice(0, 2))
const highlightedPosts = computed(() => content.slice(2, 5))
const remainingPosts = computed(() => content.slice(5))

const formatUpdatedDate = (updated: string | number | Date) => {
    const date = updated instanceof Date
        ? new Date(updated.getTime())
        : new Date(updated)

    if (Number.isNaN(date.getTime())) return ''

    const day = String(date.getUTCDate()).padStart(2, '0')
    const month = date.toLocaleString('en-GB', {
        month: 'short',
        timeZone: 'UTC'
    })

    return `${day} ${month} ${date.getUTCFullYear()}`
}

const getPostTitle = (post: BucketData) => {
    return getLocalizedValue<string>(
        post.data?.title,
        mainStore.active_locale,
        'en_GB'
    ) ?? ''
}

definePageMeta({
    layout: 'view',
    pageTransition: {
        onBeforeEnter: (el) => {
            if (window) window.scroll({top: 0})
        }
    }
})

onMounted(() => {
    siteStore.setCurrentPage(Pages.INSIGHTS)
})

</script>

<i18n lang="yaml">
en:
    title: Insights
    empty: No insights have been published yet.
    author-prefix: By
    seo:
        title: "Insights — Ideas on Digital Exhibitions & Immersive Design"
        description: "Articles and perspectives from YIPP on digital exhibition design, interactive technology, and creating memorable experiences."
nl:
    title: Inzichten
    empty: Er zijn nog geen inzichten gepubliceerd.
    author-prefix: Door
    seo:
        title: "Inzichten — Ideeën over digitale tentoonstellingen & immersief design"
        description: "Artikelen en inzichten van YIPP over digitaal tentoonstellingsontwerp, interactieve technologie en het creëren van memorabele ervaringen."
</i18n>
